import type { Collection } from '@/types/source-types';

/**
 * A pickable Data Collection. Structured collections are referenced at the
 * dataset level, unstructured ones at the collection level — hence the
 * optional `datasetId`.
 */
export interface CollectionReference {
  kind: 'dataset' | 'collection';
  collectionId: string;
  datasetId?: string;
  name: string;
  collectionName: string;
  description?: string;
}

/** A reference stripped to the ids the prompt block carries. */
export type CollectionReferenceIds = Pick<
  CollectionReference,
  'kind' | 'collectionId' | 'datasetId'
>;

export const referenceKey = (ref: CollectionReferenceIds): string =>
  ref.datasetId ? `dataset:${ref.datasetId}` : `collection:${ref.collectionId}`;

/**
 * platform-api appends attached collections to the message text as a
 * trailing block before handing it to the agent, so every persisted user
 * message carries it. Stripping it is what keeps the raw ids out of the
 * transcript when a conversation is reloaded.
 */
const TAG_BLOCK_RE =
  /\s*\[DATA COLLECTIONS\]\r?\n([\s\S]*?)\r?\n\[\/DATA COLLECTIONS\]\s*$/;

const DATASET_LINE_RE =
  /^-\s*structured dataset collection_id=(\S+)\s+dataset_id=(\S+)\s*$/;
const COLLECTION_LINE_RE =
  /^-\s*unstructured collection collection_id=(\S+)\s*$/;

export interface ParsedCollectionTags {
  /** Message text with the tag block removed. */
  cleanContent: string;
  references: CollectionReferenceIds[];
}

export const parseCollectionTags = (content: string): ParsedCollectionTags => {
  const match = content.match(TAG_BLOCK_RE);
  if (!match) return { cleanContent: content, references: [] };

  const references: CollectionReferenceIds[] = [];
  for (const line of match[1].split('\n')) {
    const dataset = line.match(DATASET_LINE_RE);
    if (dataset) {
      references.push({
        kind: 'dataset',
        collectionId: dataset[1],
        datasetId: dataset[2],
      });
      continue;
    }
    const collection = line.match(COLLECTION_LINE_RE);
    if (collection) {
      references.push({ kind: 'collection', collectionId: collection[1] });
    }
  }

  return {
    cleanContent: content.replace(TAG_BLOCK_RE, ''),
    references,
  };
};

/**
 * Display name for a reference parsed out of a message. Falls back to a
 * short id when the collection is gone or the viewer can no longer see it.
 */
export const referenceDisplayName = (
  ref: CollectionReferenceIds,
  names: ReadonlyMap<string, string>,
): string => {
  const name = names.get(referenceKey(ref));
  if (name) return name;
  const id = ref.datasetId ?? ref.collectionId;
  return `${ref.kind === 'dataset' ? 'Dataset' : 'Collection'} ${id.slice(0, 8)}`;
};

export const toReferenceNameMap = (
  references: CollectionReference[],
): Map<string, string> =>
  new Map(references.map((ref) => [referenceKey(ref), ref.name]));

/** Every dataset of every structured collection, plus each unstructured one. */
export const toCollectionReferences = (
  collections: Collection[],
): CollectionReference[] => {
  const references: CollectionReference[] = [];

  for (const collection of collections) {
    if (!collection.id) continue;

    if (collection.data_type === 'structured') {
      for (const dataset of collection.config?.datasets ?? []) {
        if (!dataset.id) continue;
        references.push({
          kind: 'dataset',
          collectionId: collection.id,
          datasetId: dataset.id,
          name: dataset.name ?? '',
          collectionName: collection.name ?? '',
          description: dataset.description ?? undefined,
        });
      }
      continue;
    }

    references.push({
      kind: 'collection',
      collectionId: collection.id,
      name: collection.name ?? '',
      collectionName: collection.name ?? '',
      description: collection.description ?? undefined,
    });
  }

  return references;
};

/** Substring match on name and parent collection. Unranked: the user chooses. */
export const filterCollectionReferences = (
  references: CollectionReference[],
  query: string,
): CollectionReference[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return references;
  return references.filter((ref) => {
    const haystack = `${ref.name} ${ref.collectionName}`.toLowerCase();
    return haystack.includes(needle);
  });
};
